import React, {useState} from 'react';
import {View, ScrollView, StyleSheet, Alert} from 'react-native';
import {Text, List, Dialog, Portal, Button} from 'react-native-paper';
import {useTranslation} from 'react-i18next';
import {useNavigation, DrawerActions} from '@react-navigation/native';
import {useAuthStore} from '../../store/useAuthStore';
import {authApi} from '../../services/api/authApi';
import GradientHeader from '../../components/common/GradientHeader';
import {colors, spacing} from '../../theme';

const SettingsScreen = () => {
  const {t} = useTranslation();
  const navigation = useNavigation();
  const {logout} = useAuthStore();
  const [logoutVisible, setLogoutVisible] = useState(false);
  const [deleteVisible, setDeleteVisible] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleLogout = async () => {
    setLogoutVisible(false);
    await logout();
  };

  const handleDeleteAccount = async () => {
    setDeleting(true);
    try {
      await authApi.deleteAccount();
      setDeleteVisible(false);
      await logout();
    } catch (e) {
      setDeleteVisible(false);
      Alert.alert(t('common.error'), e.response?.data?.message || 'Failed to delete account');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <View style={styles.container}>
      <GradientHeader title={t('settings.title')} onMenu={() => navigation.dispatch(DrawerActions.openDrawer())} />

      <ScrollView>
        <List.Section>
          <List.Item
            title={t('settings.profile')}
            left={props => <List.Icon {...props} icon="account" color={colors.primary} />}
            right={props => <List.Icon {...props} icon="chevron-right" />}
            onPress={() => navigation.navigate('Profile')}
            style={styles.listItem}
          />
          <List.Accordion
            title={t('settings.rolesAndUsers')}
            left={props => <List.Icon {...props} icon="shield-account" color={colors.primary} />}
            style={styles.listItem}>
            <List.Item
              title={t('settings.users')}
              right={props => <List.Icon {...props} icon="chevron-right" />}
              onPress={() => navigation.navigate('UserList')}
              style={styles.accordionItem}
            />
            <List.Item
              title={t('settings.roles')}
              right={props => <List.Icon {...props} icon="chevron-right" />}
              onPress={() => navigation.navigate('RoleManagement')}
              style={styles.accordionItem}
            />
          </List.Accordion>
          <List.Accordion
            title={t('settings.configuration')}
            left={props => <List.Icon {...props} icon="cog-outline" color={colors.primary} />}
            style={styles.listItem}>
            <List.Item
              title={t('config.orderSettings')}
              right={props => <List.Icon {...props} icon="chevron-right" />}
              onPress={() => navigation.navigate('OrderSettings')}
              style={styles.accordionItem}
            />
            <List.Item
              title={t('config.paymentSettings')}
              right={props => <List.Icon {...props} icon="chevron-right" />}
              onPress={() => navigation.navigate('PaymentSettings')}
              style={styles.accordionItem}
            />
            <List.Item
              title={t('config.taxSettings')}
              right={props => <List.Icon {...props} icon="chevron-right" />}
              onPress={() => navigation.navigate('TaxSettings')}
              style={styles.accordionItem}
            />
            <List.Item
              title={t('config.workingHours')}
              right={props => <List.Icon {...props} icon="chevron-right" />}
              onPress={() => navigation.navigate('WorkingHours')}
              style={styles.accordionItem}
            />
            <List.Item
              title={t('settings.billSettings')}
              right={props => <List.Icon {...props} icon="chevron-right" />}
              onPress={() => navigation.navigate('BillSettings')}
              style={styles.accordionItem}
            />
            <List.Item
              title={t('settings.language')}
              right={props => <List.Icon {...props} icon="chevron-right" />}
              onPress={() => navigation.navigate('Language')}
              style={styles.accordionItem}
            />
          </List.Accordion>
          <List.Accordion
            title={t('settings.dressesAndMeasurements')}
            left={props => <List.Icon {...props} icon="tshirt-crew" color={colors.primary} />}
            style={styles.listItem}>
            <List.Item
              title={t('settings.catalogItems')}
              right={props => <List.Icon {...props} icon="chevron-right" />}
              onPress={() => navigation.navigate('CatalogItems')}
              style={styles.accordionItem}
            />
            <List.Item
              title={t('settings.services')}
              right={props => <List.Icon {...props} icon="chevron-right" />}
              onPress={() => navigation.navigate('Services')}
              style={styles.accordionItem}
            />
            <List.Item
              title="Price Books"
              right={props => <List.Icon {...props} icon="chevron-right" />}
              onPress={() => navigation.navigate('PriceBooks')}
              style={styles.accordionItem}
            />
            <List.Item
              title={t('settings.addOns')}
              right={props => <List.Icon {...props} icon="chevron-right" />}
              onPress={() => navigation.navigate('AddOns')}
              style={styles.accordionItem}
            />
            <List.Item
              title={t('settings.taskTypes')}
              right={props => <List.Icon {...props} icon="chevron-right" />}
              onPress={() => navigation.navigate('TaskTypes')}
              style={styles.accordionItem}
            />
            <List.Item
              title={t('settings.optionTypes')}
              right={props => <List.Icon {...props} icon="chevron-right" />}
              onPress={() => navigation.navigate('OptionTypes')}
              style={styles.accordionItem}
            />
            <List.Item
              title={t('settings.measurementTypes')}
              right={props => <List.Icon {...props} icon="chevron-right" />}
              onPress={() => navigation.navigate('MeasurementTypes')}
              style={styles.accordionItem}
            />
          </List.Accordion>
          <List.Accordion
            title={t('settings.others')}
            left={props => <List.Icon {...props} icon="dots-horizontal-circle-outline" color={colors.primary} />}
            style={styles.listItem}>
            <List.Item
              title={t('settings.coupons')}
              right={props => <List.Icon {...props} icon="chevron-right" />}
              onPress={() => navigation.navigate('Coupons')}
              style={styles.accordionItem}
            />
            <List.Item
              title={t('settings.expenseCategories')}
              right={props => <List.Icon {...props} icon="chevron-right" />}
              onPress={() => navigation.navigate('ExpenseCategories')}
              style={styles.accordionItem}
            />
          </List.Accordion>
          <List.Accordion
            title={t('settings.inventory')}
            left={props => <List.Icon {...props} icon="package-variant" color={colors.primary} />}
            style={styles.listItem}>
            <List.Item
              title={t('inventory.materialUnits')}
              right={props => <List.Icon {...props} icon="chevron-right" />}
              onPress={() => navigation.navigate('MaterialUnits')}
              style={styles.accordionItem}
            />
            <List.Item
              title={t('inventory.materialTypes')}
              right={props => <List.Icon {...props} icon="chevron-right" />}
              onPress={() => navigation.navigate('MaterialTypes')}
              style={styles.accordionItem}
            />
            <List.Item
              title={t('inventory.readymadeTypes')}
              right={props => <List.Icon {...props} icon="chevron-right" />}
              onPress={() => navigation.navigate('ReadymadeTypes')}
              style={styles.accordionItem}
            />
            <List.Item
              title={t('inventory.readymadeSizes')}
              right={props => <List.Icon {...props} icon="chevron-right" />}
              onPress={() => navigation.navigate('ReadymadeSizes')}
              style={styles.accordionItem}
            />
          </List.Accordion>
        </List.Section>

        <List.Section>
          <List.Item
            title={t('settings.logout')}
            titleStyle={styles.logoutText}
            left={props => <List.Icon {...props} icon="logout" color={colors.error} />}
            onPress={() => setLogoutVisible(true)}
            style={styles.listItem}
          />
          <List.Item
            title="Delete Account"
            titleStyle={styles.deleteText}
            description="Permanently delete your account and data"
            descriptionStyle={styles.deleteDesc}
            left={props => <List.Icon {...props} icon="account-remove" color={colors.error} />}
            onPress={() => setDeleteVisible(true)}
            style={styles.listItem}
          />
        </List.Section>
      </ScrollView>

      <Portal>
        <Dialog visible={logoutVisible} onDismiss={() => setLogoutVisible(false)} style={{borderRadius: 15}}>
          <Dialog.Title>{t('settings.logout')}</Dialog.Title>
          <Dialog.Content>
            <Text>{t('settings.confirmLogout')}</Text>
          </Dialog.Content>
          <Dialog.Actions>
            <Button onPress={() => setLogoutVisible(false)}>{t('common.cancel')}</Button>
            <Button textColor={colors.error} onPress={handleLogout}>{t('settings.logout')}</Button>
          </Dialog.Actions>
        </Dialog>
        <Dialog visible={deleteVisible} onDismiss={() => setDeleteVisible(false)} style={{borderRadius: 15}}>
          <Dialog.Title>Delete Account</Dialog.Title>
          <Dialog.Content>
            <Text>Are you sure you want to delete your account? This action cannot be undone. All your data including orders, customers, and measurements will be permanently removed.</Text>
          </Dialog.Content>
          <Dialog.Actions>
            <Button onPress={() => setDeleteVisible(false)}>{t('common.cancel')}</Button>
            <Button textColor={colors.error} onPress={handleDeleteAccount} loading={deleting} disabled={deleting}>Delete</Button>
          </Dialog.Actions>
        </Dialog>
      </Portal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  listItem: {backgroundColor: colors.surface, marginBottom: 5},
  accordionItem: {backgroundColor: colors.surface, paddingLeft: spacing.xxl, marginBottom: 1},
  logoutText: {color: colors.error},
  deleteText: {color: colors.error},
  deleteDesc: {fontSize: 12, color: colors.textSecondary},
});

export default SettingsScreen;
